import {
    Campaign as CampaignIcon,
    CheckCircleOutline as CheckCircleOutlineIcon,
    Home as HomeIcon,
    MonitorHeart as MonitorHeartIcon,
    NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { Box, Button, Chip, Divider, List, ListItem, Paper, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { releaseNotes } from '../../utils/releaseNotesData';
import { PageHeader } from '../common/PageHeader';

const getMonthYear = (publishedOn) => {
    if (!publishedOn) return 'Other';
    const [monthDay, yearRaw] = publishedOn.split(',');
    const month = (monthDay || '').trim().split(' ')[0] || '';
    const year = (yearRaw || '').trim();
    return `${month} ${year}`.trim();
};

const ReleaseNotesPage = ({ darkMode, toggleDarkMode, themeVariant }) => {
    const navigate = useNavigate();
    const [selectedReleaseId, setSelectedReleaseId] = useState(releaseNotes[0]?.id || null);

    const selectedRelease = useMemo(
        () => releaseNotes.find((release) => release.id === selectedReleaseId) || releaseNotes[0],
        [selectedReleaseId],
    );

    const releaseNotesByMonth = useMemo(() => {
        const grouped = [];
        releaseNotes.forEach((release) => {
            const monthYear = getMonthYear(release.publishedOn);
            const existingGroup = grouped.find((group) => group.monthYear === monthYear);
            if (existingGroup) {
                existingGroup.items.push(release);
            } else {
                grouped.push({
                    monthYear,
                    items: [release],
                });
            }
        });
        return grouped;
    }, []);

    return (
        <Box
            sx={{
                width: '100%',
                backgroundColor: darkMode ? 'background.default' : '#f5f5f5',
                minHeight: '100vh',
            }}
        >
            <Paper
                sx={{
                    borderRadius: 0,
                    padding: 0,
                }}
            >
                <PageHeader
                    darkMode={darkMode}
                    toggleDarkMode={toggleDarkMode}
                    themeVariant={themeVariant}
                />

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, padding: 1 }}>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<HomeIcon />}
                        onClick={() => navigate('/')}
                        sx={{ mr: 2 }}
                    >
                        Home
                    </Button>
                    <NavigateNextIcon sx={{ mx: 1, color: 'text.secondary', fontSize: 20 }} />
                    <Button
                        variant="text"
                        color="secondary"
                        startIcon={<MonitorHeartIcon />}
                        onClick={() => navigate('/system-status')}
                        sx={{ mr: 2 }}
                    >
                        System Status
                    </Button>
                    <NavigateNextIcon sx={{ mx: 1, color: 'text.secondary', fontSize: 20 }} />
                    <Button variant="text" color="secondary" startIcon={<CampaignIcon />} sx={{ mr: 2 }}>
                        Release Notes
                    </Button>
                </Box>

                <Box sx={{ mx: 'auto', p: { xs: 1, sm: 2, md: 3 } }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '320px 1fr' }, gap: 2 }}>
                        <Paper sx={{ p: 2, height: 'fit-content' }}>
                            <Typography variant="h6" gutterBottom>
                                Release Notes
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Select a release to view full details here.
                            </Typography>
                            <List dense disablePadding>
                                {releaseNotesByMonth.map((group) => (
                                    <Box key={group.monthYear} sx={{ mb: 2 }}>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                textTransform: 'uppercase',
                                                fontWeight: 700,
                                                letterSpacing: 0.3,
                                                color: 'text.secondary',
                                                mb: 1,
                                            }}
                                        >
                                            {group.monthYear}
                                        </Typography>

                                        {group.items.map((release, index) => (
                                            <ListItem key={release.id} disableGutters sx={{ display: 'block' }}>
                                                <Button
                                                    fullWidth
                                                    variant={selectedReleaseId === release.id ? 'contained' : 'outlined'}
                                                    color="primary"
                                                    onClick={() => setSelectedReleaseId(release.id)}
                                                    sx={{ justifyContent: 'flex-start', textTransform: 'none', mb: 1 }}
                                                >
                                                    {release.publishedOn}
                                                </Button>
                                                {index < group.items.length - 1 && <Divider sx={{ my: 1 }} />}
                                            </ListItem>
                                        ))}
                                    </Box>
                                ))}
                            </List>
                        </Paper>

                        <Paper sx={{ p: 3 }}>
                            {selectedRelease ? (
                                <>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            flexWrap: 'wrap',
                                            gap: 1,
                                            mb: 1,
                                        }}
                                    >
                                        <Typography variant="h6">{selectedRelease.title}</Typography>
                                        <Chip label={selectedRelease.publishedOn} variant="outlined" size="small" />
                                    </Box>

                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        {selectedRelease.summary}
                                    </Typography>

                                    {selectedRelease.features && (
                                        <>
                                            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                                What is new
                                            </Typography>
                                            <List disablePadding>
                                                {selectedRelease.features.map((feature, index) => (
                                                    <ListItem key={feature.title} disableGutters sx={{ display: 'block', mb: 1.5 }}>
                                                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                                            {index + 1}. {feature.title}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {feature.description}
                                                        </Typography>
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </>
                                    )}

                                    {selectedRelease.bugFixes && (
                                        <>
                                            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                                Bug Fixes
                                            </Typography>
                                            <List disablePadding>
                                                {selectedRelease.bugFixes.map((fix, index) => (
                                                    <ListItem key={fix.title} disableGutters sx={{ display: 'block', mb: 1.5 }}>
                                                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                                            {index + 1}. {fix.title}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {fix.description}
                                                        </Typography>
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </>
                                    )}

                                    {/* only show benefits if they exist for this release */}
                                    {selectedRelease.benefits && (
                                        <>
                                            <Typography variant="subtitle1" sx={{ mb: 1, mt: 1 }}>
                                                Why this matters
                                            </Typography>
                                            <List dense disablePadding>
                                                {selectedRelease.benefits?.map((benefit) => (
                                                    <ListItem key={benefit} disableGutters sx={{ alignItems: 'flex-start', mb: 0.5 }}>
                                                        <CheckCircleOutlineIcon sx={{ fontSize: 18, mr: 1, mt: 0.1, color: 'success.main' }} />
                                                        <Typography variant="body2" color="text.secondary">
                                                            {benefit}
                                                        </Typography>
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </>
                                    )}

                                </>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No release notes available.
                                </Typography>
                            )}
                        </Paper>
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
};

export default ReleaseNotesPage;
